import { useState } from "react";
import {
  Inbox,
  Send,
  FileText,
  Trash2,
  AlertTriangle,
  Star,
  ChevronDown,
  ChevronRight,
  Plus,
  Folder,
  Settings,
  LogOut,
  Pencil,
  Trash2 as TrashIcon,
  Smartphone,
  Keyboard,
  HardDrive,
  Mail,
  X,
} from "lucide-react";
import { useFolderStore } from "../../stores/folderStore";
import { useAuthStore } from "../../stores/authStore";
import { useEmailStore } from "../../stores/emailStore";
import { useLabelStore } from "../../stores/labelStore";
import { useNotificationStore } from "../../stores/useNotificationStore";
import { useApi } from "../../hooks/useApi";
import { confirmDialog, alertDialog } from "../../stores/dialogStore";
import ThemeToggle from "../ThemeToggle";
import SettingsModal from "./SettingsModal";
import KeyboardShortcutsModal from "./KeyboardShortcutsModal";
import AccountSwitcher from "./AccountSwitcher";
import { refreshFolders } from "../../utils/refreshFolders";

const ICON_MAP: Record<string, React.ReactNode> = {
  inbox: <Inbox size={16} />,
  send: <Send size={16} />,
  "file-text": <FileText size={16} />,
  "trash-2": <Trash2 size={16} />,
  "alert-triangle": <AlertTriangle size={16} />,
  star: <Star size={16} />,
  folder: <Folder size={16} />,
};

const LABEL_COLORS = [
  "#E50914", // Red
  "#2563EB", // Blue
  "#16A34A", // Green
  "#D97706", // Amber
  "#9333EA", // Purple
  "#0D9488", // Teal
  "#DB2777", // Pink
  "#EA580C", // Orange
];

function getDomainName() {
  const host = window.location.hostname.replace(/^webmail\./, "");
  return host.toLowerCase();
}

interface FolderPanelProps {
  isOpenMobile?: boolean;
  onCloseMobile?: () => void;
  onCompose?: () => void;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1073741824) return `${(bytes / 1048576).toFixed(1)} MB`;
  return `${(bytes / 1073741824).toFixed(2)} GB`;
}

function QuotaBar() {
  const user = useAuthStore((s) => s.user);
  const folders = useFolderStore((s) => s.folders);

  const used = user?.usedBytes ?? 0;
  const quota = user?.quotaBytes ?? 0;
  const pct = quota > 0 ? Math.min((used / quota) * 100, 100) : 0;
  const isHigh = pct > 80;
  const isCritical = pct > 95;

  const totalEmails = folders.reduce((sum, f) => sum + f.totalCount, 0);

  if (!quota && !totalEmails) return null;

  return (
    <div className="px-3 py-2 border-t border-[var(--border-subtle)] flex-shrink-0 space-y-1.5">
      {/* Storage bar */}
      {quota > 0 && (
        <div>
          <div className="flex items-center justify-between text-2xs text-[var(--text-muted)] mb-1">
            <span className="flex items-center gap-1">
              <HardDrive size={10} />
              {formatBytes(used)} / {formatBytes(quota)}
            </span>
            <span className={isCritical ? "text-red-500 font-semibold" : isHigh ? "text-amber-500 font-semibold" : ""}>
              {pct.toFixed(0)}%
            </span>
          </div>
          <div className="h-1 bg-[var(--bg-tertiary)] overflow-hidden">
            <div
              className={`h-full transition-all duration-500 ${
                isCritical ? "bg-red-500" : isHigh ? "bg-amber-500" : "bg-accent"
              }`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      )}

      {/* Stats row */}
      {totalEmails > 0 && (
        <div className="flex items-center gap-3 text-2xs text-[var(--text-muted)]">
          <span className="flex items-center gap-1">
            <Mail size={10} />
            {totalEmails.toLocaleString()} mensajes
          </span>
        </div>
      )}
    </div>
  );
}

export default function FolderPanel({ isOpenMobile = false, onCloseMobile, onCompose }: FolderPanelProps) {
  const { folders, activeFolderId, setActiveFolder, removeFolder: removeFolderFromStore } = useFolderStore();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const { post: apiPost, del, patch, post } = useApi();
  const token = useAuthStore((s) => s.token);
  const { reset: resetEmails, triggerRefresh } = useEmailStore();
  const {
    labels,
    activeLabelId,
    setActiveLabel,
    addLabel,
    removeLabel,
    updateLabel,
  } = useLabelStore();
  const { installPrompt, setInstallPrompt } = useNotificationStore();

  const [customOpen, setCustomOpen] = useState(true);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [labelsOpen, setLabelsOpen] = useState(true);
  const [showNewLabel, setShowNewLabel] = useState(false);
  const [newLabelName, setNewLabelName] = useState("");
  const [newLabelColor, setNewLabelColor] = useState(LABEL_COLORS[0]);
  const [editingLabelId, setEditingLabelId] = useState<string | null>(null);
  const [editLabelName, setEditLabelName] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null);

  const systemFolders = folders.filter((f) => f.type === "system");
  const customFolders = folders.filter((f) => f.type === "custom");

  const handleFolderClick = (folderId: string) => {
    if (activeFolderId === folderId && activeLabelId === null) {
      // Same folder clicked: force a re-fetch by incrementing refreshTrigger
      resetEmails();
      triggerRefresh();
      if (onCloseMobile) onCloseMobile();
      return;
    }
    setActiveFolder(folderId);
    setActiveLabel(null);
    resetEmails();
    if (onCloseMobile) onCloseMobile();
  };

  const handleDragOverFolder = (e: React.DragEvent, folderId: string) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    if (dragOverFolderId !== folderId) {
      setDragOverFolderId(folderId);
    }
  };

  const handleDragLeaveFolder = (e: React.DragEvent, folderId: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (dragOverFolderId === folderId) {
      setDragOverFolderId(null);
    }
  };

  const handleDropOnFolder = async (e: React.DragEvent, targetFolderId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverFolderId(null);

    const rawData = e.dataTransfer.getData("application/json");
    if (!rawData) return;

    try {
      const data = JSON.parse(rawData);
      const emailIds: string[] = data.emailIds || [];
      const sourceFolder: string = data.sourceFolder || activeFolderId || "INBOX";

      if (!emailIds.length || sourceFolder === targetFolderId) return;

      // Actualizar UI optimistamente
      const { removeEmails } = useEmailStore.getState();
      removeEmails(emailIds);

      // Mover los correos mediante la API
      for (const emailId of emailIds) {
        await patch(`/mail/mails/${encodeURIComponent(sourceFolder)}/${emailId}/move`, {
          destFolder: targetFolderId,
        }).catch((err) => {
          console.error(`Failed to move email ${emailId} to ${targetFolderId}:`, err);
        });
      }

      await refreshFolders(token);
    } catch (err) {
      console.error("Error parsing drag data:", err);
    }
  };

  const handleEmptyTrash = async () => {
    const ok = await confirmDialog({
      title: "¿Vaciar papelera?",
      message: "¿Deseas eliminar permanentemente todos los correos de la papelera? Esta acción no se puede deshacer.",
      confirmText: "Vaciar papelera",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await apiPost("/mail/empty-trash", {});
      refreshFolders(token);
    } catch (err) {
      console.error("Failed to empty trash:", err);
    }
  };

  const handleEmptySpam = async () => {
    const ok = await confirmDialog({
      title: "¿Vaciar correo no deseado?",
      message: "¿Deseas eliminar permanentemente todos los correos de la carpeta de spam? Esta acción no se puede deshacer.",
      confirmText: "Vaciar spam",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await apiPost("/mail/empty-spam", {});
      refreshFolders(token);
    } catch (err) {
      console.error("Failed to empty spam:", err);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await post("/mail/folders", { name: newFolderName.trim() });
      await refreshFolders(token);
      setNewFolderName("");
      setShowNewFolder(false);
      setCustomOpen(true);
    } catch (err: any) {
      alertDialog(err.message || "Error al crear carpeta", "Error");
    }
  };

  const handleDeleteFolder = async (folderId: string, folderName: string) => {
    const ok = await confirmDialog({
      title: "¿Eliminar carpeta?",
      message: `¿Deseas eliminar la carpeta "${folderName}" y todos sus correos?`,
      confirmText: "Eliminar",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await del(`/mail/folders/${encodeURIComponent(folderId)}`);
      removeFolderFromStore(folderId);
      if (activeFolderId === folderId) {
        setActiveFolder("INBOX");
      }
      refreshFolders(token);
    } catch (err: any) {
      alertDialog(err.message || "Error al eliminar carpeta", "Error");
    }
  };

  const handleLabelClick = (labelId: string) => {
    setActiveLabel(labelId);
    setActiveFolder(null);
    resetEmails();
    if (onCloseMobile) onCloseMobile();
  };

  const handleCreateLabel = async () => {
    if (!newLabelName.trim()) return;
    try {
      const res = await apiPost<{ label?: any; id?: string; name?: string; color?: string }>("/labels", {
        name: newLabelName.trim(),
        color: newLabelColor,
      });
      const created = res.label || (res.id ? { id: res.id, name: res.name || newLabelName.trim(), color: res.color || newLabelColor } : null);
      if (created) {
        addLabel(created);
      }
      setNewLabelName("");
      setShowNewLabel(false);
    } catch (err) {
      console.error("Failed to create label:", err);
    }
  };

  const handleDeleteLabel = async (labelId: string, labelName: string) => {
    const ok = await confirmDialog({
      title: "¿Eliminar etiqueta?",
      message: `¿Deseas eliminar la etiqueta "${labelName}"? Los correos asociados no se borrarán.`,
      confirmText: "Eliminar",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await del(`/labels/${labelId}`);
      removeLabel(labelId);
      if (activeLabelId === labelId) {
        setActiveFolder("INBOX");
      }
    } catch (err) {
      console.error("Failed to delete label:", err);
    }
  };

  const handleRenameLabel = async (labelId: string) => {
    if (!editLabelName.trim()) {
      setEditingLabelId(null);
      return;
    }
    try {
      await patch(`/labels/${labelId}`, { name: editLabelName.trim() });
      updateLabel(labelId, { name: editLabelName.trim() });
      setEditingLabelId(null);
    } catch (err) {
      console.error("Failed to rename label:", err);
    }
  };

  return (
    <>
      {/* Mobile overlay backdrop */}
      {isOpenMobile && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={onCloseMobile}
        />
      )}

      {/* Sidebar container */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-60 bg-[var(--bg-secondary)] border-r border-[var(--border-color)] flex flex-col transform transition-transform duration-200 ease-in-out lg:relative lg:translate-x-0 ${
          isOpenMobile ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Account Switcher Header & Mobile Close */}
        <div className="flex items-center justify-between pt-[max(0.625rem,env(safe-area-inset-top))] sm:pt-0 border-b border-[var(--border-subtle)] flex-shrink-0">
          <div className="flex-1 min-w-0">
            <AccountSwitcher />
          </div>
          {onCloseMobile && (
            <button
              onClick={onCloseMobile}
              className="lg:hidden p-2.5 hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors border-l border-[var(--border-subtle)]"
              aria-label="Cerrar menú"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Compose Button (Sidebar Primary Action) */}
        {onCompose && (
          <div className="px-3 pt-3 pb-1.5 flex-shrink-0">
            <button
              onClick={() => {
                onCompose();
                if (onCloseMobile) onCloseMobile();
              }}
              className="w-full flex items-center justify-center gap-2 py-2.5 sm:py-1.5 px-3 text-sm sm:text-xs font-semibold bg-accent hover:bg-accent-hover active:bg-accent-hover text-white transition-all active:scale-[0.98] shadow-sm"
            >
              <Pencil size={14} />
              <span>Redactar</span>
            </button>
          </div>
        )}

      {/* Scrollable area */}
      <div className="flex-1 overflow-y-auto py-2">
        {/* System folders */}
        <div className="space-y-0.5">
          {systemFolders.map((folder) => {
            const isDragOver = dragOverFolderId === folder.id;
            return (
              <div
                key={folder.id}
                onDragOver={(e) => handleDragOverFolder(e, folder.id)}
                onDragLeave={(e) => handleDragLeaveFolder(e, folder.id)}
                onDrop={(e) => handleDropOnFolder(e, folder.id)}
                className={`flex items-center px-2 group transition-all ${
                  isDragOver ? "ring-2 ring-accent bg-accent/10 scale-[1.02]" : ""
                }`}
              >
                <button
                  onClick={() => handleFolderClick(folder.id)}
                  className={`flex-1 flex items-center gap-3 px-3 py-2.5 sm:py-1.5 text-sm sm:text-xs transition-colors ${
                    activeFolderId === folder.id
                      ? "bg-accent-light text-accent font-semibold"
                      : "text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] active:bg-[var(--bg-tertiary)]"
                  }`}
                >
                  <span
                    className={`flex-shrink-0 ${
                      activeFolderId === folder.id
                        ? "text-accent"
                        : "text-[var(--text-muted)] group-hover:text-[var(--text-secondary)]"
                    }`}
                  >
                    {ICON_MAP[folder.icon] || <Folder size={16} />}
                  </span>
                  <span className="flex-1 text-left truncate">
                    {folder.name}
                  </span>
                  {folder.unreadCount > 0 && (
                    <span className="text-xs sm:text-2xs font-semibold text-accent flex-shrink-0">
                      ({folder.unreadCount})
                    </span>
                  )}
                </button>
                {(folder.id.toLowerCase() === "trash" || folder.id.toLowerCase() === "papelera" || folder.name === "Papelera" || folder.name === "Trash") && (
                  <button
                    onClick={handleEmptyTrash}
                    className="hidden group-hover:flex items-center justify-center p-2 sm:p-1 text-xs text-[var(--text-muted)] hover:text-red-500 hover:bg-[var(--bg-tertiary)] transition-colors"
                    title="Vaciar papelera"
                    aria-label="Vaciar papelera"
                  >
                    <TrashIcon size={14} />
                  </button>
                )}
                {(folder.id.toLowerCase() === "junk" || folder.id.toLowerCase() === "spam" || folder.name === "Spam" || folder.name === "Correo no deseado" || folder.name === "Junk") && (
                  <button
                    onClick={handleEmptySpam}
                    className="hidden group-hover:flex items-center justify-center p-2 sm:p-1 text-xs text-[var(--text-muted)] hover:text-red-500 hover:bg-[var(--bg-tertiary)] transition-colors"
                    title="Vaciar spam"
                    aria-label="Vaciar correo no deseado"
                  >
                    <TrashIcon size={14} />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Custom folders section */}
        <div className="mt-3 pt-2 border-t border-[var(--border-subtle)]">
          <div className="flex items-center justify-between px-4 py-1.5 text-xs sm:text-2xs font-bold text-[var(--text-muted)] uppercase tracking-wider">
            <div
              onClick={() => setCustomOpen(!customOpen)}
              className="flex items-center gap-1.5 cursor-pointer hover:text-[var(--text-primary)] transition-colors flex-1 select-none"
            >
              {customOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              <span>Carpetas</span>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setCustomOpen(true);
                setShowNewFolder(true);
              }}
              className="p-1 hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-accent transition-colors"
              title="Nueva carpeta"
              aria-label="Nueva carpeta"
            >
              <Plus size={13} />
            </button>
          </div>

          {customOpen && (
            <div className="space-y-0.5 mt-0.5">
              {customFolders.map((folder) => {
                const isDragOver = dragOverFolderId === folder.id;
                return (
                  <div
                    key={folder.id}
                    onDragOver={(e) => handleDragOverFolder(e, folder.id)}
                    onDragLeave={(e) => handleDragLeaveFolder(e, folder.id)}
                    onDrop={(e) => handleDropOnFolder(e, folder.id)}
                    className={`flex items-center px-2 group transition-all ${
                      isDragOver ? "ring-2 ring-accent bg-accent/10 scale-[1.02]" : ""
                    }`}
                  >
                    <button
                      onClick={() => handleFolderClick(folder.id)}
                      className={`flex-1 flex items-center gap-2.5 px-2.5 py-1.5 text-xs transition-colors min-w-0 ${
                        activeFolderId === folder.id
                          ? "bg-accent-light text-accent font-semibold"
                          : "text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)]"
                      }`}
                    >
                      <Folder size={14} className="text-[var(--text-muted)] group-hover:text-[var(--text-secondary)] flex-shrink-0" />
                      <span className="flex-1 text-left truncate">{folder.name}</span>
                      {folder.unreadCount > 0 && (
                        <span className="text-2xs font-semibold text-accent flex-shrink-0">
                          ({folder.unreadCount})
                        </span>
                      )}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteFolder(folder.id, folder.name);
                      }}
                      className="hidden group-hover:flex items-center justify-center p-1 text-[var(--text-muted)] hover:text-red-500 hover:bg-[var(--bg-tertiary)] transition-colors flex-shrink-0"
                      title="Eliminar carpeta"
                      aria-label="Eliminar carpeta"
                    >
                      <TrashIcon size={12} />
                    </button>
                  </div>
                );
              })}

              {/* New folder input */}
              {showNewFolder && (
                <div className="px-3 py-1.5">
                  <input
                    autoFocus
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleCreateFolder();
                      if (e.key === "Escape") setShowNewFolder(false);
                    }}
                    placeholder="Nombre..."
                    className="w-full px-2 py-1 text-xs bg-[var(--bg-primary)] border border-[var(--border-color)] text-[var(--text-primary)] outline-none focus:border-accent mb-1"
                  />
                  <div className="flex gap-1">
                    <button
                      onClick={handleCreateFolder}
                      className="flex-1 py-0.5 text-2xs font-semibold bg-accent text-white hover:bg-accent-hover transition-colors"
                    >
                      Crear
                    </button>
                    <button
                      onClick={() => setShowNewFolder(false)}
                      className="px-2 py-0.5 text-2xs text-[var(--text-muted)] hover:bg-[var(--bg-tertiary)] transition-colors"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Labels section */}
        <div className="mt-3 pt-2 border-t border-[var(--border-subtle)]">
          <div className="flex items-center justify-between px-4 py-1 text-2xs font-bold text-[var(--text-muted)] uppercase tracking-wider">
            <div
              onClick={() => setLabelsOpen(!labelsOpen)}
              className="flex items-center gap-1.5 cursor-pointer hover:text-[var(--text-primary)] transition-colors flex-1 select-none"
            >
              {labelsOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              <span>Etiquetas</span>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLabelsOpen(true);
                setShowNewLabel(true);
              }}
              className="p-1 hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-accent transition-colors"
              title="Nueva etiqueta"
              aria-label="Nueva etiqueta"
            >
              <Plus size={13} />
            </button>
          </div>

          {labelsOpen && (
            <div className="space-y-0.5 mt-0.5">
              {labels.map((label) => (
                <div
                  key={label.id}
                  className="flex items-center px-2 group"
                >
                  {editingLabelId === label.id ? (
                    <input
                      autoFocus
                      value={editLabelName}
                      onChange={(e) => setEditLabelName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleRenameLabel(label.id);
                        if (e.key === "Escape") setEditingLabelId(null);
                      }}
                      onBlur={() => handleRenameLabel(label.id)}
                      className="flex-1 min-w-0 px-2 py-1 text-xs bg-[var(--bg-primary)] border text-[var(--text-primary)] outline-none mx-1"
                      style={{ borderColor: label.color }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <>
                      <button
                        onClick={() => handleLabelClick(label.id)}
                        className={`flex-1 flex items-center gap-2.5 px-2.5 py-1.5 text-xs transition-colors min-w-0 ${
                          activeLabelId === label.id
                            ? "bg-accent-light text-accent font-semibold"
                            : "text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)]"
                        }`}
                      >
                        <span
                          className="w-2 h-2 flex-shrink-0"
                          style={{ backgroundColor: label.color }}
                        />
                        <span
                          className="flex-1 text-left truncate"
                          style={{
                            color: activeLabelId === label.id ? label.color : "inherit",
                          }}
                        >
                          {label.name}
                        </span>
                      </button>
                      <div className="hidden group-hover:flex items-center gap-0.5 flex-shrink-0">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingLabelId(label.id);
                            setEditLabelName(label.name);
                          }}
                          className="p-1 hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                          title="Renombrar"
                          aria-label="Renombrar etiqueta"
                        >
                          <Pencil size={11} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteLabel(label.id, label.name);
                          }}
                          className="p-1 hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-red-500 transition-colors"
                          title="Eliminar etiqueta"
                          aria-label="Eliminar etiqueta"
                        >
                          <TrashIcon size={11} />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}

              {/* New label form */}
              {showNewLabel && (
                <div className="px-3 py-1.5">
                  <input
                    autoFocus
                    value={newLabelName}
                    onChange={(e) => setNewLabelName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleCreateLabel();
                      if (e.key === "Escape") setShowNewLabel(false);
                    }}
                    placeholder="Nombre..."
                    className="w-full px-2 py-1 text-xs bg-[var(--bg-primary)] border border-[var(--border-color)] text-[var(--text-primary)] outline-none focus:border-accent mb-1.5"
                  />
                  <div className="flex flex-wrap gap-1 mb-1.5">
                    {LABEL_COLORS.map((color) => (
                      <button
                        key={color}
                        onClick={() => setNewLabelColor(color)}
                        className={`w-3.5 h-3.5 transition-transform ${
                          newLabelColor === color
                            ? "scale-125 ring-1 ring-[var(--text-primary)]"
                            : ""
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={handleCreateLabel}
                      className="flex-1 py-0.5 text-2xs font-semibold bg-accent text-white hover:bg-accent-hover transition-colors"
                    >
                      Crear
                    </button>
                    <button
                      onClick={() => setShowNewLabel(false)}
                      className="px-2 py-0.5 text-2xs text-[var(--text-muted)] hover:bg-[var(--bg-tertiary)] transition-colors"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* PWA Install Button */}
      {installPrompt && (
        <div className="px-3 pb-2 flex-shrink-0">
          <button
            onClick={async () => {
              installPrompt.prompt();
              const choice = await installPrompt.userChoice;
              if (choice.outcome === "accepted") setInstallPrompt(null);
            }}
            className="w-full flex items-center justify-center gap-1.5 py-1.5 px-3 text-xs font-semibold bg-accent/10 border border-accent/30 text-accent hover:bg-accent hover:text-white transition-all shadow-2xs"
          >
            <Smartphone size={13} />
            <span>Instalar App Web</span>
          </button>
        </div>
      )}

      {/* Quota & Stats */}
      <QuotaBar />

      {/* User section (Flat & Minimalist Footer) */}
      <div className="border-t border-[var(--border-subtle)] px-3 py-2.5 pb-[max(0.625rem,env(safe-area-inset-bottom))] flex-shrink-0 bg-[var(--bg-secondary)]">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-medium text-[var(--text-secondary)] truncate min-w-0 flex-1">
            {user?.displayName || user?.email}
          </p>
          <div className="flex items-center gap-0.5 flex-shrink-0">
            <ThemeToggle size={14} />
            <button
              onClick={() => setShowShortcuts(true)}
              className="p-1.5 hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
              title="Atajos (?)"
              aria-label="Atajos de teclado"
            >
              <Keyboard size={14} />
            </button>
            <button
              onClick={() => setShowSettings(true)}
              className="p-1.5 hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
              title="Configuración"
              aria-label="Configuración"
            >
              <Settings size={14} />
            </button>
            <button
              onClick={logout}
              className="p-1.5 hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-red-500 transition-colors"
              title="Cerrar sesión"
              aria-label="Cerrar sesión"
            >
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
    <SettingsModal isOpen={showSettings} onClose={() => setShowSettings(false)} />
    <KeyboardShortcutsModal isOpen={showShortcuts} onClose={() => setShowShortcuts(false)} />
    </>
  );
}
